import React, { useState, useRef } from "react";
import { Div, Icon, Label, Touchable } from "@grassrootly/rn-core";
import { Modal, TouchableWithoutFeedback, View } from "react-native";

interface GroupActionsMenuProps {
  isOwner: boolean;
  isMember: boolean;
  canLeaveGroup: boolean;
  onAddMember?: () => void;
  onNewMatch?: () => void;
  onEditGroup?: () => void;
  onLeaveGroup?: () => void;
  onJoinGroup?: () => void;
}

export default function GroupActionsMenu({
  isOwner,
  isMember,
  canLeaveGroup,
  onAddMember,
  onNewMatch,
  onEditGroup,
  onLeaveGroup,
  onJoinGroup,
}: GroupActionsMenuProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<View>(null);

  const handlePress = () => {
    if (buttonRef.current) {
      buttonRef.current.measure((x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
        setMenuPosition({
          x: pageX - 150, // Position to the left of the button
          y: pageY + height + 5, // Position below the button
        });
        setIsVisible(true);
      });
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleMenuItemPress = (action?: () => void) => {
    handleClose();
    if (action) {
      action();
    }
  };

  const getMenuItems = () => {
    if (isOwner) {
      const ownerItems = [
        { title: "Add member", onPress: () => handleMenuItemPress(onAddMember) },
        { title: "New match", onPress: () => handleMenuItemPress(onNewMatch) },
        { title: "Edit group", onPress: () => handleMenuItemPress(onEditGroup) },
      ];
      
      // Only add "Leave group" if the owner can leave (not the only owner)
      if (canLeaveGroup) {
        ownerItems.push({ title: "Leave group", onPress: () => handleMenuItemPress(onLeaveGroup) });
      }
      
      return ownerItems;
    } else if (isMember) {
      return [
        { title: "Leave group", onPress: () => handleMenuItemPress(onLeaveGroup) },
      ];
    } else {
      return [
        { title: "Join group", onPress: () => handleMenuItemPress(onJoinGroup) },
      ];
    }
  };

  return (
    <>
      <View ref={buttonRef}>
        <Touchable onPress={handlePress}>
          <Icon name="more_vert" size={24} classNames={["mb-{5}", "mt-{8}"]} />
        </Touchable>
      </View>

      <Modal
        visible={isVisible}
        transparent
        animationType="fade"
        onRequestClose={handleClose}
      >
        <TouchableWithoutFeedback onPress={handleClose}>
          <View style={{ flex: 1 }}>
            <View style={{ 
              position: 'absolute',
              top: menuPosition.y,
              left: menuPosition.x,
              backgroundColor: 'white', 
              borderRadius: 8, 
              padding: 16, 
              minWidth: 200,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 4,
              elevation: 5,
              zIndex: 1000
            }}>
              {getMenuItems().map((item, index) => (
                <Touchable
                  key={index}
                  onPress={item.onPress}
                  classNames={["p8", "rounded"]}
                >
                  <Label type="l3Regular" classNames={["colorNeutralDarkerDark"]}>
                    {item.title}
                  </Label>
                </Touchable>
              ))}
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
}
